// @ts-strict-ignore
import { Area, AreaConfig, Column, ColumnConfig } from '@ant-design/plots';
import Icon, {
    AreaChartOutlined,
    ArrowDownOutlined,
    ArrowUpOutlined,
    CaretDownOutlined,
    DotChartOutlined,
    InfoCircleOutlined,
    LineChartOutlined,
} from '@ant-design/icons';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown/dropdown';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import { compact, flatMap, isNil, max, min } from 'lodash';
import { dayjs } from '@/extendedDayjs';
import * as React from 'react';
import { useSelector } from 'react-redux';
import {
    colorForTrendStatArrow,
    displayStatValue,
    isStatTrendValueSignificant,
    nilTextForStatisticType,
} from '../../helpers';
import Text from 'antd/lib/typography/Text';
import { DatetimeFormat } from '../../timeHelpers';
import { RootState } from '../../reducers/reducers';
import {
    DataPointType,
    OrgOverviewBucketSize,
    OrgOverviewChartType,
    OrgOverviewStatistic,
    ReleaseDurations,
    StatisticDescription,
    TrendStatus,
    StatisticCategory,
    StatisticDataType,
    StatisticSampleBucketDataPoint,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
    StatisticType,
    ReleaseStepDurations,
    AppPlatform,
    AppInfo,
    StatisticUnit,
} from '../../types';
import { selectOrgGroups, selectOrgUsers } from '../../selectors/selectors';
import { selectDestinationsData } from '../../selectors/summaryViewSelectors';
import { antGrey, colorWithAlpha, runwayColors, rwColors } from '../styles/globalStyles';
import {
    ChartColors,
    ChartStatCategoryPalettes,
    useChartColors,
    useChartStatCategoryPalettes,
    useChartTheme,
} from '../styles/themeMode';
import {
    colorForStatCategory,
    colorPaletteForStatCategory,
    displayOrgOverviewStatisticLabel,
    iconForStatCategory,
    statViewOverride,
    tooltipForScope,
    valueIconForStatisticType,
} from './orgOverviewHelpers';
import { Annotation, Datum } from '@antv/g2plot';
import { BarGraphTimeBucketDataPoint } from '../../typescriptHelpers';
import { displayTextForAppPlatform, displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import { appNameWithPlatform, platformLabelForAppName } from '../../helpers/mdmPlatformOverride';
import { OrgOverviewAreaGraph, OrgOverviewStackedBarGraph } from './OrgOverviewRecharts';
import {
    formatBucketDateLabel,
    getOrgOverviewTimeBucketLabel,
    ORG_OVERVIEW_BUCKET_SIZE_OPTIONS,
    ORG_OVERVIEW_CHART_TYPE_OPTIONS,
    DEFAULT_ORG_OVERVIEW_BUCKET_SIZE,
    DEFAULT_ORG_OVERVIEW_CHART_TYPE,
    getDefaultChartTypeForMetric,
} from './orgOverviewHelpers';
import { Flex } from 'antd/lib';

export interface StatInfoSectionProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    boldTitle?: boolean;
    tooltip?: React.ReactNode;
    isCustomTooltip?: boolean;
    cardIcon?: React.ReactNode;
    value?: React.ReactNode;
    valueIcon?: React.ReactNode;
    secondaryValue?: React.ReactNode;
    secondaryValueIcon?: React.ReactNode;
    lastDataPointDate?: string;
    trendingTooltip?: string;
}
export const StatSection: React.FC<StatInfoSectionProps> = ({
    title,
    subtitle,
    boldTitle,
    value,
    valueIcon,
    tooltip,
    isCustomTooltip,
    secondaryValue,
    secondaryValueIcon,
    lastDataPointDate,
    trendingTooltip,
    cardIcon,
}) => {
    return (
        <div
            style={{
                padding: '16px 20px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                height: '100%',
                position: 'relative',
                gap: 4,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: 16,
                    right: 20,
                }}
            >
                {cardIcon}
            </div>
            <div
                style={{
                    height: 22,
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 7,
                    alignItems: 'center',
                }}
            >
                {typeof title === 'string' ?
                    <Typography.Text
                        style={{
                            color: antGrey.grey8,
                            fontWeight: boldTitle ? 600 : 400,
                            fontSize: 14,
                            lineHeight: '22px',
                            maxWidth: '85%',
                            marginBottom: 0,
                        }}
                        ellipsis={{
                            tooltip: title,
                        }}
                    >
                        {title}
                    </Typography.Text>
                :   title}
                {typeof tooltip === 'string' ?
                    <span
                        onMouseDown={(event) => event.stopPropagation()}
                        onPointerDown={(event) => event.stopPropagation()}
                    >
                        <Tooltip
                            title={
                                <span
                                    onMouseDown={(event) => event.stopPropagation()}
                                    onPointerDown={(event) => event.stopPropagation()}
                                >
                                    <Typography.Text
                                        style={{
                                            whiteSpace: 'pre-line',
                                            color: runwayColors.white,
                                            userSelect: 'text',
                                        }}
                                    >
                                        {tooltip}
                                    </Typography.Text>
                                </span>
                            }
                        >
                            <InfoCircleOutlined
                                style={{ color: isCustomTooltip ? runwayColors.primary : antGrey.grey6 }}
                            />
                        </Tooltip>
                    </span>
                :   tooltip}
            </div>
            {subtitle && (
                <Space direction="vertical" size={0}>
                    {typeof subtitle === 'string' ?
                        <Typography.Text
                            style={{
                                color: antGrey.grey8,
                                fontWeight: 400,
                                fontSize: 12,
                                maxWidth: '85%',
                                marginBottom: 0,
                            }}
                            ellipsis={{
                                tooltip: subtitle,
                            }}
                        >
                            {subtitle}
                        </Typography.Text>
                    :   subtitle}
                </Space>
            )}
            <Space direction="vertical" size={0}>
                <div
                    style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'flex-end',
                        justifyContent: 'start',
                        flexDirection: 'row',
                        gap: 10,
                    }}
                >
                    {value && (
                        <Space direction="horizontal" align="center" size={12}>
                            {valueIcon}
                            {typeof value === 'string' ?
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey8,
                                        fontSize: 30,
                                        fontWeight: 400,
                                        lineHeight: '36px',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 170,
                                        marginBottom: 0,
                                    }}
                                    ellipsis={{
                                        tooltip: value,
                                    }}
                                >
                                    {value}
                                </Typography.Text>
                            :   value}
                        </Space>
                    )}

                    <Tooltip title={trendingTooltip}>
                        <Space direction="horizontal" align="center" size={6}>
                            {secondaryValueIcon}
                            {typeof secondaryValue === 'string' ?
                                <Typography.Text
                                    style={{
                                        color: antGrey.grey7,
                                        fontSize: 14,
                                        whiteSpace: 'nowrap',
                                        maxWidth: 70,
                                        marginBottom: 0,
                                    }}
                                    ellipsis={{
                                        tooltip: secondaryValue,
                                    }}
                                >
                                    {secondaryValue}
                                </Typography.Text>
                            :   secondaryValue}
                        </Space>
                    </Tooltip>
                </div>
                {lastDataPointDate && (
                    <Typography.Text
                        style={{
                            color: antGrey.grey7,
                            fontSize: 10,
                            maxWidth: 100,
                        }}
                        ellipsis={{
                            tooltip: lastDataPointDate,
                        }}
                    >
                        {lastDataPointDate}
                    </Typography.Text>
                )}
            </Space>
        </div>
    );
};

export interface OrgOverviewDoubleStatBaseProps {
    upperSection: StatInfoSectionProps;
    lowerSection: StatInfoSectionProps;
    isAutomation: boolean;
}

export const OrgOverviewDoubleStatBase: React.FC<OrgOverviewDoubleStatBaseProps> = ({
    isAutomation,
    upperSection,
    lowerSection,
}) => {
    const baseColor = isAutomation ? colorWithAlpha(rwColors.primary, 0.03) : runwayColors.white;
    const contrastColor = isAutomation ? colorWithAlpha(rwColors.primary, 0.1) : antGrey.grey2;
    const borderColor = isAutomation ? colorWithAlpha(rwColors.primary, 0.1) : runwayColors.white;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: 256,
                border: '1px solid',
                borderRadius: 8,
                alignItems: 'stretch',
                backgroundColor: baseColor,
                borderColor: borderColor,
            }}
        >
            <div
                style={{
                    flex: 1,
                    position: 'relative',
                }}
            >
                <StatSection {...upperSection} />
            </div>
            <div
                style={{
                    flex: 1,
                    position: 'relative',
                    backgroundColor: contrastColor,
                }}
            >
                <StatSection {...lowerSection} />
            </div>
        </div>
    );
};

export interface OrgOverviewAutomationCardProps {
    upperStat?: OrgOverviewStatistic;
    lowerStat?: OrgOverviewStatistic;
    scope: StatisticScope;
}

export const OrgOverviewAutomationCard: React.FC<OrgOverviewAutomationCardProps> = ({
    upperStat,
    lowerStat,
    scope,
}) => {
    const stats = compact([upperStat, lowerStat]);

    const [upperSection, lowerSection] = stats.map((stat) => {
        const { category, value, type, unit } = stat;

        const isAverageStat = type === StatisticType.StatAverageAutomationsCount;

        let tooltip = null;
        switch (scope) {
            case StatisticScope.App:
                tooltip = isAverageStat ? `${tooltipForScope(scope)}` : 'Total across all releases';
                break;
            case StatisticScope.AppGroup:
                tooltip = isAverageStat ? `${tooltipForScope(scope)}` : 'Total across all platforms of the app';
                break;
            case StatisticScope.Org:
                tooltip = isAverageStat ? `${tooltipForScope(scope)}` : 'Total across all apps in the organization';
                break;
            case StatisticScope.Release:
                tooltip = 'Total for this release';
                break;
            default:
                break;
        }

        const title = displayOrgOverviewStatisticLabel(type);
        const cardIcon = <Icon component={iconForStatCategory(category)} />;

        const valueIcon = (
            <Icon
                component={valueIconForStatisticType(type)}
                style={{
                    fontSize: 30,
                    color: runwayColors.primary,
                }}
            />
        );

        const valueText = displayStatValue(type, value, unit);

        const section: StatInfoSectionProps = {
            title,
            tooltip,
            cardIcon,
            value: valueText,
            valueIcon,
        };

        return section;
    });

    return <OrgOverviewDoubleStatBase upperSection={upperSection} lowerSection={lowerSection} isAutomation />;
};

export interface GraphStatDataPoint {
    x: string | number;
    y: number;
    label: string;
    category: string;
    tooltipTitle?: string;
    tooltipLabel?: string;
    tooltipGroupLabel?: string;
    tooltipUsers?: Array<{ label: string; value: number }>;
}

const MAX_TOOLTIP_USERS = 20;

function getAppNameForGraphDataPoint(appNamesAndPlatformsByAppId: Record<string, AppInfo>, appId?: string): string {
    return appId ? (appNamesAndPlatformsByAppId[appId]?.name ?? 'Unknown App') : 'Unknown App';
}

function getGraphDataPointCategory(sampleStat: StatisticTimeSeriesDataPoint): string {
    if (sampleStat.bucketId) {
        return `bucket:${sampleStat.bucketId}`;
    }

    if (sampleStat.destinationId) {
        return `destination:${sampleStat.destinationId}`;
    }

    return `app:${sampleStat.appId ?? 'unknown'}`;
}

function getGraphDataPointTooltipLabel(
    sampleStat: StatisticTimeSeriesDataPoint,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    userNamesById: Record<string, string>,
    hasUserScopedData: boolean,
): string | undefined {
    if (!hasUserScopedData) {
        return undefined;
    }

    return sampleStat.destinationName || getAppNameForGraphDataPoint(appNamesAndPlatformsByAppId, sampleStat.appId);
}

function getGraphDataPointTooltipGroupLabel(
    sampleStat: StatisticTimeSeriesDataPoint,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    hasUserScopedData: boolean,
): string | undefined {
    if (!hasUserScopedData) {
        return undefined;
    }

    return getGraphDataPointsLabel(
        appNamesAndPlatformsByAppId,
        sampleStat.appId,
        sampleStat.releaseId,
        sampleStat.platform,
    );
}

function getGraphPointsForSampleStat(
    sampleStat: StatisticTimeSeriesDataPoint,
    x: string | number,
    bucketSize: OrgOverviewBucketSize,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    userNamesById: Record<string, string>,
    hasUserScopedData: boolean,
    bucketNamesById: Record<string, string> = {},
    destinationNamesById: Record<string, string> = {},
) {
    const appLabel = getGraphDataPointsLabel(
        appNamesAndPlatformsByAppId,
        sampleStat.appId,
        sampleStat.releaseId,
        sampleStat.platform,
    );
    const appLabelWithoutRelease = getGraphDataPointsLabel(
        appNamesAndPlatformsByAppId,
        sampleStat.appId,
        undefined,
        sampleStat.platform,
    );
    const resolvedDestinationName =
        sampleStat.destinationId ?
            (destinationNamesById[sampleStat.destinationId] ?? sampleStat.destinationName ?? sampleStat.destinationId)
        :   null;
    const resolvedBucketName =
        sampleStat.bucketId ? (bucketNamesById[sampleStat.bucketId] ?? sampleStat.bucketId) : null;
    let baseLabel =
        resolvedBucketName ? `${appLabelWithoutRelease} ${resolvedBucketName}`.trim()
        : resolvedDestinationName ? `${resolvedDestinationName} ${sampleStat.releaseId ?? ''}`.trim()
        : appLabel;

    const tooltipTitle = getOrgOverviewTimeBucketLabel(sampleStat, bucketSize);

    const tooltipGroupLabel = getGraphDataPointTooltipGroupLabel(
        sampleStat,
        appNamesAndPlatformsByAppId,
        hasUserScopedData,
    );
    const valuesByUserEntries =
        sampleStat.valuesByUser ?
            Object.entries(sampleStat.valuesByUser)
                .sort((left, right) => right[1] - left[1])
                .slice(0, MAX_TOOLTIP_USERS)
        :   [];

    if (valuesByUserEntries.length > 0) {
        return [
            {
                x,
                y: sampleStat.value,
                label: baseLabel,
                category: getGraphDataPointCategory(sampleStat),
                tooltipTitle,
                tooltipLabel: getGraphDataPointTooltipLabel(
                    sampleStat,
                    appNamesAndPlatformsByAppId,
                    userNamesById,
                    hasUserScopedData,
                ),
                tooltipGroupLabel,
                tooltipUsers: valuesByUserEntries.map(([email, value]) => ({
                    label: userNamesById[email] ?? email,
                    value,
                })),
            },
        ];
    }

    return [
        {
            x,
            y: sampleStat.value,
            label: baseLabel,
            category: getGraphDataPointCategory(sampleStat),
            tooltipTitle,
            tooltipLabel: getGraphDataPointTooltipLabel(
                sampleStat,
                appNamesAndPlatformsByAppId,
                userNamesById,
                hasUserScopedData,
            ),
            tooltipGroupLabel,
        },
    ];
}

function mapDataPointsToGraphDataPoints(
    dataPoints: StatisticTimeSeriesDataPoint[],
    bucketSize: OrgOverviewBucketSize,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    userNamesById: Record<string, string>,
    hasUserScopedData: boolean,
    bucketNamesById: Record<string, string> = {},
    destinationNamesById: Record<string, string> = {},
): GraphStatDataPoint[] {
    return flatMap(dataPoints, (sampleStat) =>
        getGraphPointsForSampleStat(
            sampleStat,
            dayjs.utc(sampleStat.timestamp).valueOf(),
            bucketSize,
            appNamesAndPlatformsByAppId,
            userNamesById,
            hasUserScopedData,
            bucketNamesById,
            destinationNamesById,
        ),
    );
}

function getGraphDataPointsLabel(
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    appId?: string,
    releaseId?: string,
    platform?: string,
): string {
    const appInfo = appNamesAndPlatformsByAppId[appId];
    const appPlatform = platform?.length ? (platform as AppPlatform) : appInfo?.platform;
    const appStoreType = platform?.length ? null : appInfo?.appStoreType;
    const appName =
        appInfo ?
            appNameWithPlatform(
                appInfo.name,
                platformLabelForAppName({
                    platform: appPlatform,
                    appStoreType,
                    shouldDisplayAsMdm: appInfo.shouldDisplayAsMdm,
                    shouldOmitPlatformFromAppName: appInfo.shouldOmitPlatformFromAppName,
                }),
            )
        :   'Unknown App';
    return `${appName} ${releaseId ?? ''}`;
}

function mapBucketsPointsToGraphDataPoints(dataPoints: StatisticSampleBucketDataPoint[]): GraphStatDataPoint[] {
    return dataPoints.map((dataPoint) => {
        return {
            x: generateTimeBucket(dataPoint),
            y: dataPoint.value as number,
            label: generateTimeBucket(dataPoint),
            category: '',
        };
    });
}

const getGraphConfig: (
    type: StatisticType,
    unit: StatisticUnit,
    dataType: StatisticDataType,
    category: StatisticCategory,
    dataPoints: DataPointType[],
    bucketSize: OrgOverviewBucketSize,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    userNamesById: Record<string, string>,
    chartColors: ChartColors,
    chartPalettes: ChartStatCategoryPalettes,
    benchmarkValue?: number,
    overrideSettingsCallback?: (originalConfig: AreaConfig) => AreaConfig,
    bucketNamesById?: Record<string, string>,
    destinationNamesById?: Record<string, string>,
) => AreaConfig = (
    type,
    unit,
    dataType,
    category,
    dataPoints,
    bucketSize,
    appNamesAndPlatformsByAppId,
    userNamesById,
    chartColors,
    chartPalettes,
    benchmarkValue,
    overrideSettingsCallback,
    bucketNamesById = {},
    destinationNamesById = {},
) => {
    if (isNil(dataPoints) || dataPoints.length < 1) {
        return null;
    }
    const isSampleBuckets = dataType === StatisticDataType.SampleBuckets;
    const isDevops = category === StatisticCategory.DevOps;
    const isReleaseFrequencyStat = type === StatisticType.StatReleaseFrequency;
    const hasTimeAxis = !isSampleBuckets;
    const hasUserScopedData =
        !isSampleBuckets &&
        (dataPoints as StatisticTimeSeriesDataPoint[]).some(
            (dataPoint) => !!Object.keys(dataPoint.valuesByUser ?? {}).length,
        );
    const colors = colorPaletteForStatCategory(category, chartPalettes);

    let graphDataPoints: GraphStatDataPoint[] = [];

    if (hasTimeAxis) {
        dataPoints = [...(dataPoints as StatisticTimeSeriesDataPoint[])].sort(
            (a, b) => dayjs.utc(a.timestamp).valueOf() - dayjs.utc(b.timestamp).valueOf(),
        );
    }

    if (isSampleBuckets) {
        graphDataPoints = mapBucketsPointsToGraphDataPoints(dataPoints as StatisticSampleBucketDataPoint[]);
    } else {
        graphDataPoints = mapDataPointsToGraphDataPoints(
            dataPoints as StatisticTimeSeriesDataPoint[],
            bucketSize,
            appNamesAndPlatformsByAppId,
            userNamesById,
            hasUserScopedData,
            bucketNamesById,
            destinationNamesById,
        );
    }

    const sampleValues = graphDataPoints.map((dataPoint) => dataPoint.y) as number[];
    if (typeof benchmarkValue === 'number') {
        sampleValues.push(benchmarkValue);
    }

    const minValue = min(sampleValues);
    const maxValue = max(sampleValues);

    let gapFromBottom = Math.abs(maxValue - minValue) * 0.1;

    if (gapFromBottom === 0 && minValue !== 0) {
        gapFromBottom = Math.abs(minValue) * 0.5;
    }

    if (sampleValues.length === 1) {
        gapFromBottom = minValue * 0.5;
    }

    const minYAxis = minValue - gapFromBottom;

    const annotations: Annotation[] = [];

    const isSingleLineGraph = isDevops && !isReleaseFrequencyStat;

    const lastDataPoints: [string | number, number][] = [];
    if (isSingleLineGraph) {
        const lastDataPoint = graphDataPoints[graphDataPoints.length - 1];
        lastDataPoints.push([lastDataPoint.x, lastDataPoint.y]);
    }

    lastDataPoints.forEach(([x, y], i) => {
        annotations.push({
            type: 'dataMarker',
            top: true,
            line: null,
            position: [x, y],
            point: {
                style: {
                    stroke: 'white',
                    r: 8,
                    lineWidth: 4,
                    fill: colors[i],
                },
            },
        });
    });

    if (typeof benchmarkValue === 'number') {
        annotations.push({
            type: 'line',
            start: ['min', benchmarkValue],
            end: ['max', benchmarkValue],
            style: {
                stroke: chartColors.axisLabel,
                lineDash: [6, 6],
                lineWidth: 2,
            },
        });
    }

    const defaultTimeRange = 90 * 24 * 60 * 60 * 1000;
    const config: AreaConfig = {
        xField: 'x',
        yField: 'y',
        animation: false,
        padding: isDevops ? [0, 0, 0, 0] : [0, 0, 27, 0],
        isStack: false,
        legend: false,
        startOnZero: false,
        width: 30,
        yAxis: {
            label: null,
            grid: null,
            tickLine: null,
            range: [0, 0.8],
            nice: true,
            min: minYAxis,
        },
        annotations,
        tooltip: {
            showMarkers: true,
            showTitle: true,
            domStyles: {
                'g2-tooltip': {
                    backgroundColor: chartColors.tooltipBg,
                    color: chartColors.tooltipText,
                },
            },
            crosshairs: {
                line: {
                    style: {
                        stroke: colorWithAlpha(rwColors.primary, 0.8),
                        lineDash: [2, 2],
                        lineWidth: 2,
                    },
                },
            },
            shared: false,
            fields: ['x', 'y', 'label', 'category'],
            formatter: (datum: Datum) => {
                return {
                    name: datum.label,
                    value: displayStatValue(type, datum.y, unit),
                };
            },
        },
        xAxis: {
            ...(hasTimeAxis && graphDataPoints.length === 1 ?
                {
                    min: (graphDataPoints[0].x as number) - defaultTimeRange / 2,
                    max: (graphDataPoints[0].x as number) + defaultTimeRange / 2,
                    nice: false,
                    tickCount: 5,
                }
            :   {
                    nice: true,
                    tickMethod: hasTimeAxis ? 'time' : 'cat',
                    tickInterval: hasTimeAxis ? 24 * 3600 * 1000 * 7 : null,
                }),
            range: [0, 0.9],
            line: null,
            grid: {
                line: {
                    style: {
                        stroke: chartColors.gridLine,
                        lineWidth: 2,
                    },
                },
            },
            nice: false,
            label:
                isDevops && !hasTimeAxis ? null : (
                    {
                        offsetX: 10,
                        offsetY: 0,
                        style: (_, index, items) => {
                            return {
                                textAlign:
                                    index === 0 ? 'left'
                                    : index === items.length - 1 ? 'right'
                                    : 'center',
                            };
                        },
                        autoHide: {
                            type: 'reserveBoth',
                            cfg: {
                                minGap: 50,
                            },
                        },
                    }
                ),
        },
        line: {
            style: {
                lineWidth: 4,
                lineCap: 'butt',
                lineJoin: 'miter',
            },
        },
        areaStyle: {
            fill: chartPalettes.areaFill,
            fillOpacity: 0,
        },
        seriesField: isSingleLineGraph ? null : 'category',
        data: graphDataPoints,
        meta: {
            x:
                hasTimeAxis ?
                    {
                        type: 'time',
                        formatter: (value) => {
                            return formatBucketDateLabel(dayjs.utc(Number(value)).format('YYYY-MM-DD'), bucketSize);
                        },
                    }
                :   undefined,
            y: {
                type: 'linear',
                formatter: (value) => displayStatValue(type, value, unit),
            },
        },
        point: {
            shape: 'circle',
            size: 2,
            style: {
                strokeOpacity: 0,
            },
        },
        color: isSingleLineGraph ? colors[0] : colors,
    };

    return overrideSettingsCallback ? overrideSettingsCallback(config) : config;
};

const getRechartsGraphData = (
    type: StatisticType,
    unit: StatisticUnit,
    dataType: StatisticDataType,
    category: StatisticCategory,
    dataPoints: DataPointType[],
    bucketSize: OrgOverviewBucketSize,
    appNamesAndPlatformsByAppId: Record<string, AppInfo>,
    userNamesById: Record<string, string>,
    chartColors: ChartColors,
    chartPalettes: ChartStatCategoryPalettes,
    benchmarkValue?: number,
    bucketNamesById: Record<string, string> = {},
    destinationNamesById: Record<string, string> = {},
) => {
    if (isNil(dataPoints) || dataPoints.length < 1) {
        return null;
    }

    const isSampleBuckets = dataType === StatisticDataType.SampleBuckets;
    const isDevops = category === StatisticCategory.DevOps;
    const isReleaseFrequencyStat = type === StatisticType.StatReleaseFrequency;
    const hasTimeAxis = !isSampleBuckets;
    const isSingleLineGraph = isDevops && !isReleaseFrequencyStat;
    const hasUserScopedData =
        !isSampleBuckets &&
        (dataPoints as StatisticTimeSeriesDataPoint[]).some(
            (dataPoint) => !!Object.keys(dataPoint.valuesByUser ?? {}).length,
        );

    let graphDataPoints: GraphStatDataPoint[] = [];

    if (hasTimeAxis) {
        dataPoints = [...(dataPoints as StatisticTimeSeriesDataPoint[])].sort(
            (a, b) => dayjs.utc(a.timestamp).valueOf() - dayjs.utc(b.timestamp).valueOf(),
        );
    }

    if (isSampleBuckets) {
        graphDataPoints = mapBucketsPointsToGraphDataPoints(dataPoints as StatisticSampleBucketDataPoint[]);
    } else {
        graphDataPoints = mapDataPointsToGraphDataPoints(
            dataPoints as StatisticTimeSeriesDataPoint[],
            bucketSize,
            appNamesAndPlatformsByAppId,
            userNamesById,
            hasUserScopedData,
            bucketNamesById,
            destinationNamesById,
        );
    }

    if (isSingleLineGraph) {
        graphDataPoints = graphDataPoints.map((dataPoint) => ({
            ...dataPoint,
            category: 'value',
        }));
    }

    return {
        data: graphDataPoints,
        colors: [],
        hasTimeAxis,
        hasUserScopedData,
        hideXAxis: isDevops && !hasTimeAxis,
        benchmarkValue,
        bucketSize,
        chartColors,
        chartPalettes,
        type,
        unit,
    };
};

export const arrowForTrend: (change: number, trend: TrendStatus, arrowSize: number) => React.ReactNode = (
    change,
    trend,
    arrowSize,
) => {
    let node: React.ReactNode;
    const color = colorForTrendStatArrow(trend);

    if (change < 0) {
        node = <ArrowDownOutlined style={{ fontSize: arrowSize, color }} />;
    } else if (change > 0) {
        node = <ArrowUpOutlined style={{ fontSize: arrowSize, color }} />;
    } else {
        node = <></>;
    }

    return node;
};
export interface OrgOverviewStatCardBaseProps extends OrgOverviewStatistic {
    orgId?: string;
    appNamesAndPlatformsByAppId: Record<string, AppInfo>;
    bucketNamesById?: Record<string, string>;
    isLoading?: boolean;
    scope: StatisticScope;
    isEmpty?: boolean;
    destinationId?: string;
    destinationName?: string;
    platform?: string;
    shouldDisplayDestination?: boolean;
    useNewChartsLibrary?: boolean;
    bucketSize?: OrgOverviewBucketSize;
    supportsCustomBuckets?: boolean;
    onBucketSizeChange?: (metricType: StatisticType, bucketSize: OrgOverviewBucketSize) => void;
    chartType?: OrgOverviewChartType;
    onChartTypeChange?: (metricType: StatisticType, chartType: OrgOverviewChartType) => void;
}

const getTrendingSinceDate: (dataPoints?: DataPointType[]) => string = (dataPoints) => {
    if (isNil(dataPoints) || dataPoints?.length == 0) {
        return '';
    }

    const first = dataPoints[0];
    let timestamp: string;

    const timeSeries = first as StatisticTimeSeriesDataPoint;
    if (!isNil(timeSeries) && !isNil(timeSeries.timestamp)) {
        timestamp = timeSeries.sampleStartedAt ?? timeSeries.timestamp;
    } else {
        const sampleBucket = first as StatisticSampleBucketDataPoint;
        if (!isNil(sampleBucket) && !isNil(sampleBucket.sampleStartedAt)) {
            timestamp = sampleBucket.sampleStartedAt;
        }
    }

    if (isNil(timestamp)) {
        return '';
    }

    return formatCalendarDateLabel(timestamp, true);
};

const getMostRecentDataPointTimeframeString: (
    dataPoints?: DataPointType[],
    bucketSize?: OrgOverviewBucketSize,
) => string = (dataPoints, bucketSize = DEFAULT_ORG_OVERVIEW_BUCKET_SIZE) => {
    if (isNil(dataPoints) || dataPoints?.length == 0) {
        return '';
    }
    const mostRecent = dataPoints[dataPoints.length - 1];

    // DevOps metrics use StatisticSampleBucketDataPoint. Not showing for StatisticTimeSeriesDataPoint
    const sampleBucket = mostRecent as StatisticSampleBucketDataPoint;
    if (!isNil(sampleBucket) && !isNil(sampleBucket.sampleStartedAt) && !isNil(sampleBucket.sampleEndedAt)) {
        return generateTimeBucket(sampleBucket);
    }
    const timeSeries = mostRecent as StatisticTimeSeriesDataPoint;
    if (!isNil(timeSeries) && !isNil(timeSeries.timestamp)) {
        return getOrgOverviewTimeBucketLabel(timeSeries, bucketSize);
    }
    return '';
};

function getCardDropdownControl<T extends string>(
    selectedValue: T,
    options: Array<{ label: string; value: T }>,
    onSelect?: (value: T) => void,
) {
    if (!onSelect) {
        return null;
    }

    const selectedLabel = options.find((option) => option.value === selectedValue)?.label ?? selectedValue;

    return (
        <Dropdown
            trigger={['click']}
            menu={{
                items: options.map((option) => ({
                    key: option.value,
                    label: option.label,
                })),
                onClick: ({ key }) => onSelect(key as T),
            }}
        >
            <div
                onMouseDown={(event) => event.stopPropagation()}
                onClick={(event) => event.stopPropagation()}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    height: 24,
                    padding: '0 8px',
                    border: `1px solid ${antGrey.grey4}`,
                    borderRadius: 6,
                    backgroundColor: runwayColors.white,
                    cursor: 'pointer',
                }}
            >
                <Text
                    style={{
                        color: antGrey.grey8,
                        fontSize: 12,
                        lineHeight: '12px',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {selectedLabel}
                </Text>
                <CaretDownOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 10,
                    }}
                />
            </div>
        </Dropdown>
    );
}

const CHART_TYPE_TOGGLE_ICONS: Record<OrgOverviewChartType, React.ReactNode> = {
    [OrgOverviewChartType.Line]: <LineChartOutlined style={{ fontSize: 16, color: antGrey.grey7 }} />,
    [OrgOverviewChartType.Scatter]: <DotChartOutlined style={{ fontSize: 16, color: antGrey.grey7 }} />,
    [OrgOverviewChartType.MedianBand]: <AreaChartOutlined style={{ fontSize: 16, color: antGrey.grey7 }} />,
};

// Mirrors the theme mode toggle: the button shows the NEXT type in the rotation and clicking advances to it.
function getChartTypeToggle(
    chartType: OrgOverviewChartType,
    options: Array<{ label: string; value: OrgOverviewChartType }>,
    isSwitching: boolean,
    onSelect: (value: OrgOverviewChartType) => void,
) {
    const currentIndex = options.findIndex((option) => option.value === chartType);
    const nextOption = options[(currentIndex + 1) % options.length];
    const tooltipTitle = `Switch to ${nextOption.label.toLowerCase()}`;

    return (
        <Tooltip title={tooltipTitle}>
            <Button
                type="text"
                size="small"
                aria-label={tooltipTitle}
                loading={isSwitching}
                onMouseDown={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                    event.stopPropagation();
                    onSelect(nextOption.value);
                }}
                icon={CHART_TYPE_TOGGLE_ICONS[nextOption.value]}
            />
        </Tooltip>
    );
}

export const getTrendingTooltip: (
    type: StatisticType,
    statName: string,
    change: number,
    trendText: string,
    dataPoints?: DataPointType[],
) => string = (_type, statName, change, trendText, dataPoints) => {
    const direction =
        change > 0 ? 'upwards'
        : change < 0 ? 'downwards'
        : 'neutral';

    const trendingSinceDate = getTrendingSinceDate(dataPoints);
    const since = trendingSinceDate != '' ? ` since ${trendingSinceDate}` : '';

    return `${statName} has been trending ${direction} by ${trendText}${since}`;
};

function resolveDescription(d: StatisticDescription | string | undefined): { text: string; isCustom: boolean } {
    if (d && typeof d === 'object') {
        return { text: d.description, isCustom: d.isCustom };
    }
    return { text: (d as string | undefined) ?? '', isCustom: false };
}

export const OrgOverviewStatCardBase: React.FC<OrgOverviewStatCardBaseProps> = ({
    orgId,
    type,
    category,
    displayText,
    description,
    unit,
    dataPoints,
    value,
    benchmarkValue,
    trend,
    trendStatus,
    isLoading,
    scope,
    dataType,
    appNamesAndPlatformsByAppId,
    isEmpty,
    destinationId,
    destinationName,
    platform,
    shouldDisplayDestination,
    useNewChartsLibrary = false,
    bucketSize = DEFAULT_ORG_OVERVIEW_BUCKET_SIZE,
    supportsCustomBuckets = false,
    onBucketSizeChange,
    chartType,
    onChartTypeChange,
    bucketNamesById = {},
}) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const chartPalettes = useChartStatCategoryPalettes();
    const orgUsers = useSelector((state: RootState) => (orgId ? selectOrgUsers(state, orgId) : []));
    const orgGroupsById = useSelector((state: RootState) => (orgId ? selectOrgGroups(state, orgId) : {}));
    const orgDestinationsData = useSelector((state: RootState) =>
        orgId ? selectDestinationsData(state, orgId) : undefined,
    );
    const destinationNamesById = React.useMemo(
        () =>
            (orgDestinationsData?.appDestinations ?? []).reduce<Record<string, string>>((acc, appData) => {
                (appData.destinations ?? []).forEach((destination) => {
                    acc[destination.stepConfigId] = destination.name;
                    if (destination.destinationId) {
                        acc[destination.destinationId] = destination.name;
                    }
                });
                return acc;
            }, {}),
        [orgDestinationsData],
    );
    const userNamesById = React.useMemo(
        () => ({
            ...Object.values(orgGroupsById).reduce(
                (acc, group) => {
                    acc[group.id] = group.name;
                    return acc;
                },
                {} as Record<string, string>,
            ),
            ...orgUsers.reduce(
                (acc, user) => {
                    const displayName = user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user.email;
                    acc[user.id] = displayName;
                    if (user.email) {
                        acc[user.email] = displayName;
                    }
                    return acc;
                },
                {} as Record<string, string>,
            ),
        }),
        [orgGroupsById, orgUsers],
    );
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const destination = getDisplayNameForDestination(destinationId, destinationName, platform);
    const subtitle = !isEmpty && shouldDisplayDestination ? destination : null;
    const resolvedUnit = unit;
    const resolvedTrendStatus = trendStatus ?? TrendStatus.Neutral;
    const graphConfig = React.useMemo(
        () =>
            getGraphConfig(
                type,
                resolvedUnit,
                dataType,
                category,
                dataPoints,
                bucketSize,
                appNamesAndPlatformsByAppId,
                userNamesById,
                chartColors,
                chartPalettes,
                benchmarkValue,
                undefined,
                bucketNamesById,
                destinationNamesById,
            ),
        [
            type,
            resolvedUnit,
            dataType,
            category,
            dataPoints,
            bucketSize,
            appNamesAndPlatformsByAppId,
            userNamesById,
            chartColors,
            chartPalettes,
            benchmarkValue,
            bucketNamesById,
            destinationNamesById,
        ],
    );
    const rechartsGraphData = React.useMemo(
        () =>
            useNewChartsLibrary ?
                getRechartsGraphData(
                    type,
                    resolvedUnit,
                    dataType,
                    category,
                    dataPoints,
                    bucketSize,
                    appNamesAndPlatformsByAppId,
                    userNamesById,
                    chartColors,
                    chartPalettes,
                    benchmarkValue,
                    bucketNamesById,
                    destinationNamesById,
                )
            :   null,
        [
            useNewChartsLibrary,
            type,
            resolvedUnit,
            dataType,
            category,
            dataPoints,
            bucketSize,
            appNamesAndPlatformsByAppId,
            userNamesById,
            chartColors,
            chartPalettes,
            benchmarkValue,
            bucketNamesById,
            destinationNamesById,
        ],
    );
    const hasMultipleSeries = React.useMemo(() => {
        const graphDataPoints = rechartsGraphData?.data ?? [];
        return graphDataPoints.some((dataPoint) => dataPoint.category !== graphDataPoints[0]?.category);
    }, [rechartsGraphData]);
    // A median + band across a single series is just the line, so hide the option (and fall back if persisted).
    const preferredChartType = chartType ?? getDefaultChartTypeForMetric(type);
    const resolvedChartType =
        preferredChartType === OrgOverviewChartType.MedianBand && !hasMultipleSeries ?
            DEFAULT_ORG_OVERVIEW_CHART_TYPE
        :   preferredChartType;
    const chartTypeOptions =
        hasMultipleSeries ?
            ORG_OVERVIEW_CHART_TYPE_OPTIONS
        :   ORG_OVERVIEW_CHART_TYPE_OPTIONS.filter((option) => option.value !== OrgOverviewChartType.MedianBand);
    const [isChartTypeSwitching, startChartTypeTransition] = React.useTransition();
    const bucketSizeControl =
        supportsCustomBuckets && onBucketSizeChange ?
            getCardDropdownControl(bucketSize, ORG_OVERVIEW_BUCKET_SIZE_OPTIONS, (nextBucketSize) =>
                onBucketSizeChange(type, nextBucketSize),
            )
        :   null;
    const chartTypeControl =
        useNewChartsLibrary && onChartTypeChange ?
            getChartTypeToggle(resolvedChartType, chartTypeOptions, isChartTypeSwitching, (nextChartType) =>
                startChartTypeTransition(() => onChartTypeChange(type, nextChartType)),
            )
        :   null;

    let formattedValue =
        value !== undefined && value !== null ?
            displayStatValue(type, value, resolvedUnit)
        :   <Typography.Text
                type="secondary"
                style={{
                    fontSize: 18,
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    color: antGrey.grey6,
                    maxWidth: 170,
                    lineHeight: '36px',
                    marginBottom: 0,
                }}
                ellipsis={{
                    tooltip: nilTextForStatisticType(type),
                }}
            >
                {nilTextForStatisticType(type)}
            </Typography.Text>;

    const { text: descriptionText, isCustom: isCustomDescription } = resolveDescription(description);
    const tooltip = descriptionText || tooltipForScope(scope, type);

    const shouldShowSecondaryValue = !isNil(trend);
    const isTrendSignificant = isStatTrendValueSignificant(trend, type);

    const hasValue = !isEmpty && value !== undefined && value !== null;

    const upperSection: StatInfoSectionProps = {
        title:
            isEmpty ?
                <Skeleton
                    paragraph={false}
                    style={{
                        maxWidth: 120,
                        width: '100%',
                    }}
                />
            :   title,
        subtitle: subtitle,
        value: isEmpty ? '' : formattedValue,
        secondaryValue:
            isEmpty ? ''
            : isTrendSignificant ? shouldShowSecondaryValue && displayStatValue(type, trend, resolvedUnit)
            : hasValue ?
                <Text
                    style={{
                        color: antGrey.grey5,
                    }}
                >
                    No trend
                </Text>
            :   '',
        lastDataPointDate: getMostRecentDataPointTimeframeString(dataPoints, bucketSize),
        secondaryValueIcon:
            shouldShowSecondaryValue && isTrendSignificant && resolvedTrendStatus ?
                arrowForTrend(trend, resolvedTrendStatus, 14)
            :   null,
        tooltip: isEmpty ? null : tooltip,
        isCustomTooltip: !isEmpty && isCustomDescription,
        trendingTooltip:
            shouldShowSecondaryValue && isTrendSignificant ?
                getTrendingTooltip(type, title, trend, displayStatValue(type, trend, resolvedUnit), dataPoints)
            :   '',
        cardIcon: (
            <Flex align="center" gap={16}>
                {chartTypeControl}
                {bucketSizeControl}
            </Flex>
        ),
    };

    const overrideView = statViewOverride({
        type,
        value,
        dataPoints,
    });

    if (overrideView) {
        upperSection.secondaryValue = '';
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <OrgOverviewCardSkeleton />
            :   <>
                    <div
                        style={{
                            height: 108,
                            flexShrink: 0,
                        }}
                    >
                        <StatSection {...upperSection} />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 148,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div style={{ flex: 1, minHeight: typeof benchmarkValue === 'number' ? 124 : 148 }}>
                            {overrideView ??
                                (useNewChartsLibrary ?
                                    rechartsGraphData ?
                                        <OrgOverviewAreaGraph
                                            {...rechartsGraphData}
                                            chartType={resolvedChartType}
                                            config={
                                                graphConfig ?
                                                    { ...graphConfig, color: undefined, data: undefined }
                                                :   graphConfig
                                            }
                                        />
                                    :   <EmptyDataPlaceholderChart />
                                : graphConfig ?
                                    <Area
                                        {...graphConfig}
                                        theme={chartTheme}
                                        style={{
                                            height: '100%',
                                            width: '100%',
                                        }}
                                    />
                                :   <EmptyDataPlaceholderChart />)}
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

export const OrgOverviewCardSkeleton: React.FC = () => (
    <>
        <div style={{ padding: '16px 20px' }}>
            <Skeleton active round={false} paragraph={{ rows: 3 }} />
        </div>
        <div style={{ flex: 1, display: 'flex', padding: '0 20px 20px' }}>
            <Skeleton.Node active style={{ width: '100%', height: '100%', minHeight: 120 }}>
                <div />
            </Skeleton.Node>
        </div>
    </>
);

const EmptyDataPlaceholderChart: React.FC = () => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            height: '100%',
        }}
    >
        <Typography.Text style={{ color: antGrey.grey6, width: 162, marginLeft: 20 }}>Not enough data.</Typography.Text>
    </div>
);

export const OrgOverviewStatEmptyCard: React.FC = () => {
    return (
        <OrgOverviewStatCardBase
            isEmpty
            key={''}
            value={0}
            displayText=""
            description=""
            unit={null}
            category={null}
            type={null}
            dataType={null}
            dataPoints={[]}
            scope={null}
            appNamesAndPlatformsByAppId={{}}
        />
    );
};

export const OrgOverviewStatLoadingCard: React.FC = () => {
    return (
        <OrgOverviewStatCardBase
            isLoading
            key={''}
            value={0}
            displayText=""
            description=""
            unit={null}
            category={null}
            type={null}
            dataType={null}
            dataPoints={[]}
            scope={null}
            appNamesAndPlatformsByAppId={{}}
        />
    );
};

export const OrgOverviewStatCard = React.memo<OrgOverviewStatCardBaseProps>((props) => {
    return <OrgOverviewStatCardBase {...props} />;
});

const getBarGraphConfig: (
    type: StatisticType,
    data: BarGraphTimeBucketDataPoint[],
    chartColors: ChartColors,
    overrideSettingsCallback?: (originalConfig: ColumnConfig) => ColumnConfig,
) => ColumnConfig = (type, data, chartColors, overrideSettingsCallback) => {
    if (isNil(data) || data.length < 1) {
        return null;
    }

    const oneDay = 86400;

    // map values to have min value half a day
    data = data.map((d) => ({
        ...d,
        value: d.value < oneDay / 2 && d.value !== 0 ? oneDay / 2 : d.value,
    }));

    const maxValue = Math.max(...data.map((d) => d.value));

    const oneFifthSegment = Math.ceil(maxValue / 5);

    const roundedSegmentToDays = Math.ceil(oneFifthSegment / oneDay) * oneDay;

    const config: ColumnConfig = {
        data,
        isStack: true,
        xField: 'timeBucket',
        yField: 'value',
        appendPadding: [20, 20, 6, 20],
        animation: false,
        meta: {
            value: {
                alias: 'Time (seconds)',
                formatter: (value) => {
                    if (value === 0) {
                        return '0d';
                    }
                    if (value <= oneDay / 2) {
                        return '< 1d';
                    }
                    return displayStatValue(type, value);
                },
            },
        },
        xAxis: {
            label: null,
        },
        yAxis: {
            tickInterval: roundedSegmentToDays,
        },
        seriesField: 'type',
        label: null,
        legend: {
            offsetY: 20,
        },
        tooltip: {
            domStyles: {
                'g2-tooltip': {
                    backgroundColor: chartColors.tooltipBg,
                    color: chartColors.tooltipText,
                },
            },
        },
    };

    return overrideSettingsCallback ? overrideSettingsCallback(config) : config;
};

const getRechartsBarGraphData = (data: BarGraphTimeBucketDataPoint[]) => {
    if (isNil(data) || data.length < 1) {
        return null;
    }

    const oneDay = 86400;

    return data.map((datum) => ({
        ...datum,
        value: datum.value < oneDay / 2 && datum.value !== 0 ? oneDay / 2 : datum.value,
    }));
};

export interface OrgOverviewDurationsBarGraphStatProps extends OrgOverviewStatistic {
    isLoading?: boolean;
    scope: StatisticScope;
    useNewChartsLibrary?: boolean;
    bucketSize?: OrgOverviewBucketSize;
    supportsCustomBuckets?: boolean;
    onBucketSizeChange?: (metricType: StatisticType, bucketSize: OrgOverviewBucketSize) => void;
}

const OrgOverviewDurationsBarGraphStatBase: React.FC<OrgOverviewDurationsBarGraphStatProps> = ({
    type,
    displayText,
    description,
    dataPoints,
    unit,
    isLoading,
    scope,
    useNewChartsLibrary = false,
    bucketSize = DEFAULT_ORG_OVERVIEW_BUCKET_SIZE,
    supportsCustomBuckets = false,
    onBucketSizeChange,
}) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const { text: descriptionText, isCustom: isCustomDescription } = resolveDescription(description);
    const tooltip = descriptionText || tooltipForScope(scope, type);
    const bucketSizeControl =
        supportsCustomBuckets && onBucketSizeChange ?
            getCardDropdownControl(bucketSize, ORG_OVERVIEW_BUCKET_SIZE_OPTIONS, (nextBucketSize) =>
                onBucketSizeChange(type, nextBucketSize),
            )
        :   null;

    const upperSection: StatInfoSectionProps = {
        title,
        tooltip,
        isCustomTooltip: isCustomDescription,
        cardIcon: (
            <Flex align="center" gap={16}>
                {bucketSizeControl}
            </Flex>
        ),
    };

    const data = React.useMemo(
        () =>
            flatMap(dataPoints as Array<StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint>, (dataPoint) => {
                const durations = dataPoint.value as ReleaseDurations;
                const timeBucket = getTimeBucketKey(dataPoint);
                const axisLabel = getTimeBucketLabel(dataPoint, bucketSize);

                return [
                    {
                        type: 'Deploy',
                        value: durations.deployTimeInSeconds,
                        total: durations.totalTimeInSeconds,
                        timeBucket,
                        axisLabel,
                        tooltipTitle: axisLabel,
                    },
                    {
                        type: 'Work',
                        value: durations.workTimeInSeconds,
                        total: durations.totalTimeInSeconds,
                        timeBucket,
                        axisLabel,
                        tooltipTitle: axisLabel,
                    },
                    {
                        type: 'Waiting',
                        value: durations.waitTimeInSeconds,
                        total: durations.totalTimeInSeconds,
                        timeBucket,
                        axisLabel,
                        tooltipTitle: axisLabel,
                    },
                ];
            }),
        [dataPoints, bucketSize],
    );

    const columnConfig = React.useMemo(() => getBarGraphConfig(type, data, chartColors), [type, data, chartColors]);
    const rechartsData = React.useMemo(
        () => (useNewChartsLibrary ? getRechartsBarGraphData(data) : null),
        [data, useNewChartsLibrary],
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                gridColumn: 'auto / span 3',
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <OrgOverviewCardSkeleton />
            :   <>
                    <div
                        style={{
                            height: 56,
                            flexShrink: 0,
                        }}
                    >
                        <StatSection {...upperSection} />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                        }}
                    >
                        {useNewChartsLibrary ?
                            rechartsData ?
                                <OrgOverviewStackedBarGraph
                                    {...columnConfig}
                                    data={rechartsData}
                                    type={type}
                                    unit={unit}
                                    chartColors={chartColors}
                                />
                            :   <EmptyDataPlaceholderChart />
                        : columnConfig ?
                            <Column
                                {...columnConfig}
                                theme={chartTheme}
                                style={{
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                        :   <EmptyDataPlaceholderChart />}
                    </div>
                </>
            }
        </div>
    );
};

export const OrgOverviewDurationsBarGraphStat = React.memo(OrgOverviewDurationsBarGraphStatBase);

export interface OrgOverviewReleaseStepDurationsProps extends OrgOverviewStatistic {
    isLoading?: boolean;
    scope: StatisticScope;
    isWorkflowsCIStepEnabled: boolean;
    useNewChartsLibrary?: boolean;
}

export const OrgOverviewReleaseStepDurations: React.FC<OrgOverviewReleaseStepDurationsProps> = ({
    type,
    category,
    displayText,
    description,
    unit,
    dataPoints,
    isLoading,
    scope,
    isWorkflowsCIStepEnabled,
    useNewChartsLibrary = false,
}) => {
    const chartTheme = useChartTheme();
    const chartColors = useChartColors();
    const title = displayText || displayOrgOverviewStatisticLabel(type);
    const { text: descriptionText, isCustom: isCustomDescription } = resolveDescription(description);
    const tooltip = descriptionText || tooltipForScope(scope, type);

    const typedDataPoints = dataPoints as Array<StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint>;
    const upperSection: StatInfoSectionProps = {
        title,
        tooltip,
        isCustomTooltip: isCustomDescription,
        cardIcon: (
            <Icon
                component={iconForStatCategory(category)}
                style={{
                    fontSize: 14,
                    color: colorForStatCategory(category),
                }}
            />
        ),
    };

    const data: BarGraphTimeBucketDataPoint[] = flatMap(typedDataPoints, (dataPoint) => {
        const timeBucket =
            useNewChartsLibrary ? getReleaseStepDurationTimeBucketLabel(dataPoint)
            : 'timestamp' in dataPoint ? formatCalendarDateLabel(dataPoint.timestamp, true)
            : generateTimeBucket(dataPoint, true);

        const releaseStepDurations = dataPoint.value as ReleaseStepDurations;

        return releaseStepDurations.durations.map(({ releaseStepType, duration }) => {
            return {
                timeBucket,
                type: displayTextForReleaseStepType(releaseStepType, null, null, isWorkflowsCIStepEnabled),
                value: duration,
                total: releaseStepDurations.totalDuration,
            };
        });
    });

    const columnConfig = getBarGraphConfig(type, data, chartColors, (originalConfig) => {
        return {
            ...originalConfig,
            yAxis: {
                ...originalConfig.yAxis,
                label: null,
            },
            legend: false,
        };
    });
    const rechartsData = useNewChartsLibrary ? getRechartsBarGraphData(data) : null;

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minHeight: 256,
                border: '1px solid',
                borderColor: antGrey.grey4,
                borderRadius: 8,
                alignItems: 'stretch',
            }}
        >
            {isLoading ?
                <OrgOverviewCardSkeleton />
            :   <>
                    <div
                        style={{
                            height: 56,
                            flexShrink: 0,
                        }}
                    >
                        <StatSection {...upperSection} />
                    </div>
                    <div
                        style={{
                            flex: 1,
                            minHeight: 200,
                            backgroundColor: 'var(--rw-chart-container-bg)',
                        }}
                    >
                        {useNewChartsLibrary ?
                            rechartsData ?
                                <OrgOverviewStackedBarGraph
                                    {...columnConfig}
                                    data={rechartsData}
                                    type={type}
                                    unit={unit}
                                    chartColors={chartColors}
                                    showLegend={false}
                                    hideYAxis
                                />
                            :   <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        height: '100%',
                                    }}
                                >
                                    <Text
                                        style={{
                                            color: antGrey.grey6,
                                            width: 162,
                                            marginLeft: 20,
                                        }}
                                    >
                                        Waiting for more release data.
                                    </Text>
                                </div>

                        : columnConfig ?
                            <Column
                                {...columnConfig}
                                theme={chartTheme}
                                style={{
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                        :   <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        width: 162,
                                        marginLeft: 20,
                                    }}
                                >
                                    Waiting for more release data.
                                </Text>
                            </div>
                        }
                    </div>
                </>
            }
        </div>
    );
};

function generateTimeBucket(dataPoint: StatisticSampleBucketDataPoint, includeYear = false) {
    return `${formatCalendarDateLabel(dataPoint.sampleStartedAt, includeYear)} - ${formatCalendarDateLabel(
        dataPoint.sampleEndedAt,
        includeYear,
    )}`;
}

function getTimeBucketKey(dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint) {
    if ('timestamp' in dataPoint) {
        return dataPoint.sampleStartedAt ?? dataPoint.timestamp;
    }

    return dataPoint.sampleStartedAt;
}

function getReleaseStepDurationTimeBucketLabel(
    dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint,
) {
    const bucketLabel = getTimeBucketLabel(dataPoint, DEFAULT_ORG_OVERVIEW_BUCKET_SIZE);
    if ('timestamp' in dataPoint && dataPoint.releaseId) {
        return `${dataPoint.releaseId} (${bucketLabel})`;
    }
    return bucketLabel;
}

function getTimeBucketLabel(
    dataPoint: StatisticSampleBucketDataPoint | StatisticTimeSeriesDataPoint,
    bucketSize: OrgOverviewBucketSize = DEFAULT_ORG_OVERVIEW_BUCKET_SIZE,
) {
    if ('timestamp' in dataPoint) {
        return getOrgOverviewTimeBucketLabel(dataPoint, bucketSize);
    }

    return generateTimeBucket(dataPoint, true);
}

function formatCalendarDateLabel(value: string, includeYear = false) {
    const format = includeYear ? DatetimeFormat.ShortMonthDateYear : DatetimeFormat.ShortMonthDate;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return dayjs(value).format(format);
    }

    return dayjs.utc(value).local().format(format);
}

function getDisplayNameForDestination(destinationId?: string, destinationName?: string, platform?: string) {
    if (destinationName?.length) {
        return destinationName;
    }

    if (platform?.length) {
        return displayTextForAppPlatform(platform as AppPlatform);
    }

    return destinationId;
}
